import React, { useCallback, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { Button, LinearProgress, Typography } from '@mui/material';
import { useMeetingHubContext } from '../../MeetingHubContext';
import MeetingService from 'services/MeetingService';
import useRequest from 'hooks/useRequest';
import { useHubEvent } from 'hooks/useHubEvent';
import Card from 'components/shared/Card';
import IPCheckTable from '../IPCheckTable';
import { Attendee, Vote, VotingSession } from 'store/types/Meeting';
import MeetingHubEvents from 'store/enums/MeetingHubActions';
import { VoteValueType } from 'store/enums/VoteTypes';
import { ipQuestion } from 'util/meeting';
import { defaultSnackbarErrorProps } from 'util/layout';

import commonStyles from 'styles/common.module.scss';
import { useAuthContext } from 'AuthContext';

interface IPCheckVotingViewCardProps {
  ipCheckVoting: VotingSession;
}

const IPCheckVotingViewCard: React.FunctionComponent<IPCheckVotingViewCardProps> = ({ ipCheckVoting }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isStaff } = useAuthContext();
  const { connection, meetingId, attendeeId, attendees, canViewAllVoters } = useMeetingHubContext();

  const getVotesRequest = useMemo(
    () => (!meetingId ? undefined : () => MeetingService.getVotes(meetingId, ipCheckVoting.motionId)),
    [meetingId, ipCheckVoting.motionId]
  );
  const { data: motionVotes = [], refetch: refetchMotionVotes } = useRequest<Vote[]>(getVotesRequest);

  const votingPercentage =
    ipCheckVoting.voters?.length && ipCheckVoting.voters?.length > 0
      ? Number((motionVotes.length / ipCheckVoting.voters?.length) * 100)
      : 0;

  const votesByAttendeeId = useMemo(() => {
    const map = new Map<string, Vote>();
    motionVotes.forEach((v) => map.set(v.attendeeId, v));
    return map;
  }, [motionVotes]);

  const sessionVoters = useMemo(
    () => attendees?.filter((a) => ipCheckVoting.voters?.includes(a.id)) ?? [],
    [attendees, ipCheckVoting.voters]
  );

  const currentUserVoter = useMemo(() => sessionVoters.find((v) => v.id === attendeeId), [attendeeId, sessionVoters]);
  const currentUserVote = useMemo(() => votesByAttendeeId.get(attendeeId)?.value, [attendeeId, votesByAttendeeId]);

  const castVote = useCallback(
    (attendee: Attendee, value: VoteValueType) => {
      const vote: Vote = {
        meetingId,
        motionId: ipCheckVoting.motionId,
        attendeeId: attendee.id,
        attendeeName: attendee.name,
        affiliate: attendee.affiliate ?? '',
        value,
      };

      MeetingService.saveVote(vote)
        .then((vote) => {
          enqueueSnackbar(`Response cast: ${vote.value}`, { variant: 'success' });
        })
        .catch((error: string) => {
          enqueueSnackbar(`Failed to cast response`, defaultSnackbarErrorProps);
          console.error('Error casting response:', error);
        });
    },
    [enqueueSnackbar, ipCheckVoting.motionId, meetingId]
  );

  useHubEvent(connection, MeetingHubEvents.VotesCleared, refetchMotionVotes);
  useHubEvent(connection, MeetingHubEvents.VoteCasted, refetchMotionVotes);

  return (
    <Card title={<Typography variant="h6">{ipCheckVoting.motionStatement}</Typography>} bordered>
      <Typography>{ipQuestion}</Typography>
      {currentUserVoter && !isStaff && (
        <div className="mt-3">
          <div className={commonStyles.buttonGroup}>
            <Button
              color={currentUserVote === VoteValueType.Yes ? 'secondary' : 'info'}
              variant={currentUserVote === VoteValueType.Yes ? 'contained' : 'outlined'}
              disabled={false}
              onClick={() => castVote(currentUserVoter, VoteValueType.Yes)}
            >
              {VoteValueType.Yes}
            </Button>
            <Button
              color={currentUserVote === VoteValueType.No ? 'secondary' : 'info'}
              variant={currentUserVote === VoteValueType.No ? 'contained' : 'outlined'}
              disabled={false}
              onClick={() => castVote(currentUserVoter, VoteValueType.No)}
            >
              {VoteValueType.No}
            </Button>
          </div>
        </div>
      )}
      {canViewAllVoters && (
        <div className="mt-3">
          <LinearProgress
            variant="determinate"
            color={ipCheckVoting.isVotingAvailable ? 'secondary' : 'inherit'}
            value={votingPercentage}
          />
          <IPCheckTable
            disableVoting={!ipCheckVoting.isVotingAvailable}
            voters={sessionVoters}
            votesByAttendeeId={votesByAttendeeId}
            castVote={castVote}
          />
        </div>
      )}
      {!currentUserVoter && !canViewAllVoters && (
        <Typography sx={{ fontWeight: 700, marginTop: 3 }}>IP check in progress</Typography>
      )}
    </Card>
  );
};

export default IPCheckVotingViewCard;
